import {
  Badge,
  Card,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Search } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api/client";
import { formatDateTime } from "@/lib/format";

interface AdminUserRow {
  id: string;
  clinic_id: string;
  clinic_name: string;
  email: string;
  full_name: string;
  role: string;
  specialty: string | null;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

export function AdminUsersPage() {
  const [search, setSearch] = useState("");
  const [debSearch] = useDebouncedValue(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-all-users"],
    queryFn: async () => {
      const { data } = await api.get<AdminUserRow[]>("/admin/users");
      return data;
    },
  });

  const filtered = (data ?? []).filter((u) => {
    if (!debSearch.trim()) return true;
    const q = debSearch.toLowerCase();
    return (
      u.full_name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.clinic_name.toLowerCase().includes(q) ||
      u.role.toLowerCase().includes(q)
    );
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title="Barcha xodimlar"
        subtitle={`${filtered.length} ta foydalanuvchi`}
      />

      <Card padding="md">
        <TextInput
          rightSection={<Search size={18} strokeWidth={2} />}
          placeholder="F.I.SH, login, klinika, rol..."
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </Card>

      <Card padding={0} radius="xl">
        <Table.ScrollContainer minWidth={900}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Klinika</Table.Th>
                <Table.Th>F.I.SH</Table.Th>
                <Table.Th>Login</Table.Th>
                <Table.Th>Roli</Table.Th>
                <Table.Th>Mutaxassisligi</Table.Th>
                <Table.Th>Holati</Table.Th>
                <Table.Th>Oxirgi kirish</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr><Table.Td colSpan={7}><Loader size="sm" /></Table.Td></Table.Tr>
              )}
              {filtered.map((u) => (
                <Table.Tr key={u.id}>
                  <Table.Td>
                    <Badge variant="light" color="brand" radius="sm">
                      {u.clinic_name}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Text size="sm" fw={600}>{u.full_name}</Text></Table.Td>
                  <Table.Td><Text size="sm" ff="monospace">{u.email}</Text></Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="violet" radius="sm">{u.role}</Badge>
                  </Table.Td>
                  <Table.Td><Text size="sm">{u.specialty ?? "—"}</Text></Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={u.is_active ? "green" : "gray"} radius="sm">
                      {u.is_active ? "Faol" : "Nofaol"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {u.last_login_at ? formatDateTime(u.last_login_at) : "—"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
